import { TokenProvider } from '~/hooks/auth/useAccessTokenProvider';
import { RequestParameters } from './backend-client';
import { BackendV2Client } from './backend-v2-client';
import { APIType } from './types';
import { handleError } from '~/utils/errors';

export {
    DEFAULT_POLL_INTERVAL_MS,
    DEFAULT_TIMEOUT_MS,
} from './backend-v2-client';

export type CreateRegistrationTaskPayload = {
    task_id: string;
    model_name: string;
    source_image: string;
    target_image: string;
    matrix: number[];
    matrix_shape: number[];
    matrix_convention: 'row_first' | 'column_first';
};

export class RegistrationClient extends BackendV2Client {
    protected get taskPathSegment(): string {
        return 'reg';
    }

    constructor(
        baseURL: string,
        tokenProvider: TokenProvider,
        sessionID: string
    ) {
        super(baseURL, tokenProvider, sessionID, APIType.REGISTRATION);
    }

    /**
     * Creates an adapt+ / registration task with pre-uploaded scans.
     * POSTs `/v2/reg/` with a transformation matrix alongside the two
     * dataset IDs the scans live under.
     */
    public async createRegistrationTask(
        request: RequestParameters,
        payload: CreateRegistrationTaskPayload
    ): Promise<unknown> {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;

            const url = this.envelopeURL('v2/reg/', params);

            const headers = {
                ...(await this.defaultHeaders()),
                'Content-Type': 'application/json',
                skip_dicom_validation: 'true',
            };

            const body = JSON.stringify(payload);

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
            });

            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to create registration task: ${response.status}`
                    ),
                    text
                );
                return false;
            }

            return response.json();
        } catch (error) {
            handleError(error, 'Error creating task with pre-uploaded scans.');
            return false;
        }
    }
}
